import { RiShoppingCartFill } from '@remixicon/react'
import { format, parseISO } from 'date-fns'
import { useEffect, useMemo, useState } from 'react'

import type { Cart } from '@/api/production-carts/production-carts.types'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { DATE_FORMATS } from '@/constants/app'
import { cn } from '@/lib/utils'
import { isCartBlockedByShipDate } from '@/utils/carts'

interface AssignCartProps {
  carts: Cart[]
  cart: string | undefined
  disabled?: boolean
  className?: string
  /** Latest ship_date the cart must accommodate. Carts whose
   *  max_allowed_ship_date is earlier are hidden. */
  maxShipDate?: string | null
  /** Carts already in use by this order — pinned to the top so the guys stop
   *  scrolling the full list to keep one order together. */
  suggestedCartIds?: number[]
  /** Fires on open/close. */
  onOpenChange?: (open: boolean) => void
  /** Pointer or focus reached the picker — time to load suggestions before it opens. */
  onIntent?: () => void
  onCartChange: (cart: string) => void
}

const CartOption = ({ cart, suggested = false }: { cart: Cart; suggested?: boolean }) => (
  <SelectItem
    value={cart?.id?.toString()}
    className={cn(
      '*:[span]:last:flex-1 *:[span]:last:justify-between',
      // Green, not just grouped — the shop missed the carts when they only moved up.
      suggested && 'text-black-200 bg-green-100 focus:bg-green-200/30'
    )}
  >
    <span>{cart?.name}</span>
    <span className='text-grey-500 text-xs'>
      {cart?.max_allowed_ship_date
        ? `max ${format(parseISO(cart.max_allowed_ship_date), DATE_FORMATS.dotsShort)}`
        : 'empty'}
    </span>
  </SelectItem>
)

export const AssignCart = ({
  carts,
  cart,
  disabled = false,
  onCartChange,
  className,
  maxShipDate,
  suggestedCartIds,
  onOpenChange,
  onIntent
}: AssignCartProps) => {
  const [isOpen, setIsOpen] = useState(false)

  // Taken in only while the list is shut: reordering options under a pointer already on
  // its way down would assign the cart below the one aimed at.
  const [shownIds, setShownIds] = useState<number[]>(suggestedCartIds ?? [])

  useEffect(() => {
    if (isOpen) return
    const next = suggestedCartIds ?? []
    // Callers rebuild the array every render; keep the old one when the ids match.
    setShownIds((prev) =>
      prev.length === next.length && prev.every((id, i) => id === next[i]) ? prev : next
    )
  }, [isOpen, suggestedCartIds])

  const visibleCarts = useMemo(
    () => carts.filter((c) => !isCartBlockedByShipDate(c, maxShipDate)),
    [carts, maxShipDate]
  )

  // Suggestions never widen the list — a cart blocked by ship date stays hidden.
  const [suggested, rest] = useMemo(() => {
    const ids = new Set(shownIds)
    if (!ids.size) return [[], visibleCarts] as const
    return [visibleCarts.filter((c) => ids.has(c.id)), visibleCarts.filter((c) => !ids.has(c.id))] as const
  }, [visibleCarts, shownIds])

  return (
    <Select
      disabled={visibleCarts.length === 0 || disabled}
      value={cart}
      onValueChange={onCartChange}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (open) onIntent?.()
        onOpenChange?.(open)
      }}
    >
      <SelectTrigger
        className={cn('w-full', className)}
        onPointerEnter={onIntent}
        onFocus={onIntent}
      >
        <RiShoppingCartFill className='text-grey-500' />
        <SelectValue placeholder={`Assign Cart`} />
      </SelectTrigger>
      <SelectContent>
        {suggested.length ? (
          <>
            <SelectGroup>
              <SelectLabel className='text-grey-700'>On this order</SelectLabel>
              {suggested.map((cart) => (
                <CartOption
                  key={cart?.id}
                  cart={cart}
                  suggested
                />
              ))}
            </SelectGroup>
            <SelectSeparator />
          </>
        ) : null}
        {rest.map((cart) => (
          <CartOption
            key={cart?.id}
            cart={cart}
          />
        ))}
      </SelectContent>
    </Select>
  )
}
